import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, FileMusic, Clock, CheckCircle2, Sparkles } from "lucide-react";

const STATUS_META = {
  brief_draft:       { label: "Brief in Progress",        color: "text-white/70 border-white/20 bg-white/5" },
  lyrics_pending:    { label: "Lyrics Pending",           color: "text-blue-300 border-blue-400/40 bg-blue-400/5" },
  lyrics_review:     { label: "Lyrics Ready to Review",   color: "text-gold border-gold/40 bg-gold/5" },
  lyrics_submitted:  { label: "In Music Production",      color: "text-blue-300 border-blue-400/40 bg-blue-400/5" },
  variations_ready:  { label: "Songs Ready to Preview",   color: "text-gold border-gold/40 bg-gold/5" },
  completed:         { label: "Completed",                color: "text-green-400 border-green-400/40 bg-green-400/5" },
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  const startNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects");
      nav(`/dashboard/projects/${data.id}/brief`);
    } finally {
      setCreating(false);
    }
  };

  const inProgress = projects.filter((p) => !["completed"].includes(p.status));
  const done = projects.filter((p) => p.status === "completed");

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2">Customer Portal</div>
            <h1 className="font-heading text-3xl md:text-4xl">Welcome, {user?.name?.split(" ")[0]}</h1>
            <p className="text-white/60 mt-2">Start a new song project or manage existing ones.</p>
          </div>
          <button onClick={startNew} disabled={creating} className="btn-gold" data-testid="dashboard-new-project">
            <Plus size={16} /> {creating ? "Creating…" : "Start New Song Project"}
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-12">
          <Stat icon={Sparkles} value={projects.length} label="Total Projects" />
          <Stat icon={Clock} value={inProgress.length} label="In Progress" />
          <Stat icon={CheckCircle2} value={done.length} label="Completed" />
        </div>

        <h2 className="font-heading text-2xl mb-5">Your Projects</h2>
        {loading ? (
          <div className="text-white/50">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="bg-ink-900 border border-ink-700 p-12 text-center" data-testid="dashboard-empty">
            <FileMusic className="text-gold mx-auto mb-4" size={40} />
            <h3 className="font-heading text-xl mb-2">No projects yet</h3>
            <p className="text-white/60 mb-6">Begin with a comprehensive business brief — our AI then drafts lyrics, you refine them, and Perry produces your songs.</p>
            <button onClick={startNew} className="btn-gold">Start First Project</button>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((p) => {
              const status = STATUS_META[p.status] || { label: p.status, color: "text-white/70 border-white/20 bg-white/5" };
              const bp = p.brief?.business_profile || {};
              return (
                <Link
                  to={`/dashboard/projects/${p.id}`}
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  className="block bg-ink-900 border border-ink-700 hover:border-gold transition-colors p-5"
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="font-heading text-lg">{bp.business_name || "Untitled Brief"}</h3>
                      <div className="text-white/50 text-xs mt-1">
                        Started {new Date(p.created_at).toLocaleDateString()}
                        {p.variations?.length > 0 && ` • ${p.variations.length} song${p.variations.length>1?"s":""} delivered`}
                      </div>
                    </div>
                    <div className={`text-[10px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${status.color}`}>{status.label}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-ink-900 border border-ink-700 p-6 flex items-center gap-4">
      <div className="w-12 h-12 border border-gold/40 flex items-center justify-center"><Icon className="text-gold" /></div>
      <div>
        <div className="font-heading text-3xl">{value}</div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-white/55 font-semibold">{label}</div>
      </div>
    </div>
  );
}
